// Shared in-flight geo lookup. First caller initiates the fetch; later
// callers await the same promise so we never issue duplicate requests.
// Cached for the lifetime of the page via window.__geoPromise.

export type GeoInfo = {
  country_code?: string;
  ip?: string;
};

const FALLBACK_GEO_ENDPOINT = 'https://country-code-redirect-worker.hismileteeth.com/';

// Cloudflare gets a priority window to answer; if it doesn't return a valid code
// in time, we open an additional window for whichever source (worker or Shopify)
// returns a valid code first. Both are pre-warmed by preloadGeo.
const WORKER_PRIORITY_MS = 1500;
const FALLBACK_MS = 4000;

export const getGeoInfo = (): Promise<GeoInfo | null> => {
  if (!window.__geoPromise) {
    window.__geoPromise = fetch(window.__geoEndpoint || FALLBACK_GEO_ENDPOINT)
      .then((r) => (r.ok ? (r.json() as Promise<GeoInfo>) : null))
      .catch(() => null);
  }
  return window.__geoPromise;
};

export const isValidIsoCode = (code: unknown): code is string =>
  typeof code === 'string' && /^[A-Z]{2}$/.test(code);

const getWorkerIsoCode = async (): Promise<string | undefined> => {
  const data = await getGeoInfo();
  const code = data?.country_code;
  return isValidIsoCode(code) ? code : undefined;
};

type ShopifyContextSuggestions = {
  detected_values?: {
    country?: {
      handle?: string;
    };
  };
};

const SHOPIFY_GEO_ENDPOINT = '/browsing_context_suggestions.json?country[enabled]=true';

const getShopifyGeoInfo = (): Promise<ShopifyContextSuggestions | null> => {
  if (!window.__shopifyGeoPromise) {
    window.__shopifyGeoPromise = fetch(SHOPIFY_GEO_ENDPOINT, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? (r.json() as Promise<ShopifyContextSuggestions>) : null))
      .catch(() => null);
  }
  return window.__shopifyGeoPromise;
};

const getShopifyDetectedIsoCode = async (): Promise<string | undefined> => {
  const data = await getShopifyGeoInfo();
  const code = data?.detected_values?.country?.handle;
  return isValidIsoCode(code) ? code : undefined;
};

// Resolves with the first VALID (ISO-2) code from the given sources, or undefined
// if they all resolve invalid or the timeout elapses first. 
const firstValidIsoCode = (
  sources: Promise<string | undefined>[],
  ms: number,
): Promise<string | undefined> => {
  const timeout = new Promise<undefined>((resolve) => {
    setTimeout(() => resolve(undefined), ms);
  });
  const firstValid = Promise.any(
    sources.map(async (source) => (await source) || Promise.reject(new Error('invalid'))),
  ).catch(() => undefined);
  return Promise.race([firstValid, timeout]);
};

export const resolveIsoCode = async (
  workerMs: number = WORKER_PRIORITY_MS,
  fallbackMs: number = FALLBACK_MS,
): Promise<string | undefined> => {
  const worker = getWorkerIsoCode().catch(() => undefined);
  const shopify = getShopifyDetectedIsoCode().catch(() => undefined);

  const workerResult = await firstValidIsoCode([worker], workerMs);
  if (workerResult) return workerResult;

  return firstValidIsoCode([worker, shopify], fallbackMs);
};
